/**
 * Dev-only performance debug dialog (mobile-friendly toggles).
 */

import * as C from "../config/constants.js";
import { normalizeSkyBackgroundPreference } from "./sky-background-prefs.js";
import { runtimeFlags } from "./runtime-flags.js";

var DEV_PERF_CHIP_SELECTOR = ".dev-fps-overlay, .dev-stats-overlay, .dev-perf-dialog, .dev-perf-dialog *";
/** Ignore backdrop dismiss briefly after open so mobile ghost-clicks do not instantly close. */
export var DEV_PERF_DIALOG_OPEN_GUARD_MS = 500;

/**
 * @param {EventTarget | null | undefined} target
 * @returns {boolean}
 */
export function isDevPerfUiTarget(target) {
  if (!target || typeof target.closest !== "function") return false;
  return !!target.closest(DEV_PERF_CHIP_SELECTOR);
}

/**
 * @returns {HTMLElement | null}
 */
function ensureBoardShell() {
  if (typeof document === "undefined") return null;
  var app = document.getElementById("app");
  if (!app || !app.parentNode) return null;
  var shell = document.getElementById("boardShell");
  if (!shell) {
    shell = document.createElement("div");
    shell.id = "boardShell";
    app.parentNode.insertBefore(shell, app);
    shell.appendChild(app);
  }
  return shell;
}

/**
 * Mount perf chips/dialog on #boardShell (outside scaled #app) so mobile taps register.
 * @param {Record<string, HTMLElement | null | undefined>} dom
 */
export function relocateDevPerfOverlaysToShell(dom) {
  if (typeof document === "undefined" || !dom) return;
  var shell = ensureBoardShell();
  if (!shell) return;
  var keys = ["devFpsOverlay", "devStatsOverlay", "devPerfDialog"];
  for (var i = 0; i < keys.length; i++) {
    var el = dom[keys[i]];
    if (el && el.parentNode !== shell) {
      shell.appendChild(el);
    }
  }
}

/**
 * @param {HTMLElement | null | undefined} el
 * @param {() => void} openDialog
 */
function wireDevPerfChipOpen(el, openDialog) {
  if (!el || typeof openDialog !== "function" || el.dataset.devPerfChipBound) return;
  el.dataset.devPerfChipBound = "1";

  var openFromChip = function (event) {
    if (event && typeof event.stopPropagation === "function") event.stopPropagation();
    openDialog();
  };

  el.addEventListener("touchend", function (event) {
    if (event.cancelable) event.preventDefault();
    openFromChip(event);
  }, { passive: false });

  el.addEventListener("click", function (event) {
    openFromChip(event);
  });
}

/**
 * @param {HTMLElement | null} el
 * @param {boolean} open
 */
export function setDevPerfDialogOpen(el, open) {
  if (!el) return;
  var isOpen = !!open;
  el.classList.toggle("hidden", !isOpen);
  el.setAttribute("aria-hidden", isOpen ? "false" : "true");
}

/**
 * Sync perf dialog toggle labels from current runtime/game state.
 * @param {import("./game.js").Game | null | undefined} game
 */
export function syncDevPerfDialogUi(game) {
  if (!game || !game.dom || typeof game._isDevMode !== "function" || !game._isDevMode()) return;
  var dom = game.dom;

  if (dom.devPerfBgBtn) {
    var bgOn = !!runtimeFlags.drawSkySeaBackground;
    dom.devPerfBgBtn.textContent = bgOn ? "Background: On" : "Background: Off";
    dom.devPerfBgBtn.setAttribute("aria-pressed", bgOn ? "true" : "false");
  }
  if (dom.devPerfStarsBtn) {
    var starsOn = runtimeFlags.drawSkyStars !== false;
    dom.devPerfStarsBtn.textContent = starsOn ? "Stars: On" : "Stars: Off";
    dom.devPerfStarsBtn.setAttribute("aria-pressed", starsOn ? "true" : "false");
  }
  if (dom.devPerfSkyBtn) {
    var skyPref = normalizeSkyBackgroundPreference(
      (game._skyBackgroundPrefs && game._skyBackgroundPrefs.preference)
        || (game.skyClock && game.skyClock.cfg && game.skyClock.cfg.SKY_BACKGROUND_MODE)
    );
    var skyLabel = skyPref === C.SKY_BACKGROUND_MODES.AUTO
      ? "Sky mode: Auto"
      : (skyPref === C.SKY_BACKGROUND_MODES.DYNAMIC ? "Sky mode: Dynamic" : "Sky mode: Static");
    dom.devPerfSkyBtn.textContent = skyLabel;
    dom.devPerfSkyBtn.setAttribute(
      "aria-pressed",
      skyPref === C.SKY_BACKGROUND_MODES.AUTO ? "false" : "true"
    );
  }
  if (dom.devPerfSoundsBtn) {
    var soundsOn = !game._devPerfSoundsMuted;
    dom.devPerfSoundsBtn.textContent = soundsOn ? "Sounds: On" : "Sounds: Off";
    dom.devPerfSoundsBtn.setAttribute("aria-pressed", soundsOn ? "true" : "false");
  }
  if (dom.devPerfParticlesBtn) {
    var particlesOn = runtimeFlags.drawParticles !== false;
    dom.devPerfParticlesBtn.textContent = particlesOn ? "Particles: On" : "Particles: Off";
    dom.devPerfParticlesBtn.setAttribute("aria-pressed", particlesOn ? "true" : "false");
  }
  if (dom.devPerfPopupsBtn) {
    var popupsOn = runtimeFlags.drawScorePopups !== false;
    dom.devPerfPopupsBtn.textContent = popupsOn ? "Score popups: On" : "Score popups: Off";
    dom.devPerfPopupsBtn.setAttribute("aria-pressed", popupsOn ? "true" : "false");
  }
  if (dom.devPerfLayoutBtn) {
    var layoutPref = game.layoutModePreference || "auto";
    dom.devPerfLayoutBtn.textContent = layoutPref === "vertical"
      ? "Layout: Vertical"
      : (layoutPref === "horizontal" ? "Layout: Horizontal" : "Layout: Auto");
    dom.devPerfLayoutBtn.setAttribute("aria-pressed", layoutPref === "auto" ? "false" : "true");
  }
  if (dom.devPerfBoundsBtn) {
    var boundsOn = !!runtimeFlags.showLayoutSectionBorders;
    dom.devPerfBoundsBtn.textContent = boundsOn ? "Bounds: On" : "Bounds: Off";
    dom.devPerfBoundsBtn.setAttribute("aria-pressed", boundsOn ? "true" : "false");
  }
  if (dom.devPerfFallCollidersBtn) {
    var fallCollidersOn = !!runtimeFlags.showFallColliderDebug;
    dom.devPerfFallCollidersBtn.textContent = fallCollidersOn
      ? "Fall colliders: On"
      : "Fall colliders: Off";
    dom.devPerfFallCollidersBtn.setAttribute("aria-pressed", fallCollidersOn ? "true" : "false");
  }
}

/**
 * @param {import("./game.js").Game} game
 * @param {Record<string, HTMLElement | null>} dom
 */
export function wireDevPerfDialog(game, dom) {
  if (!game || !dom) return;

  var dialogOpenedAt = 0;

  function openDialog() {
    dialogOpenedAt = Date.now();
    if (typeof game.openDevPerfDialog === "function") game.openDevPerfDialog();
  }

  relocateDevPerfOverlaysToShell(dom);
  wireDevPerfChipOpen(dom.devFpsOverlay, openDialog);
  wireDevPerfChipOpen(dom.devStatsOverlay, openDialog);
  if (dom.devPerfDialogCloseBtn) {
    dom.devPerfDialogCloseBtn.addEventListener("click", function () {
      if (typeof game.closeDevPerfDialog === "function") game.closeDevPerfDialog();
    });
  }
  if (dom.devPerfDialogBackdrop) {
    dom.devPerfDialogBackdrop.addEventListener("click", function () {
      if (Date.now() - dialogOpenedAt < DEV_PERF_DIALOG_OPEN_GUARD_MS) return;
      if (typeof game.closeDevPerfDialog === "function") game.closeDevPerfDialog();
    });
  }
  if (dom.devPerfBgBtn) {
    dom.devPerfBgBtn.addEventListener("click", function () {
      if (typeof game.toggleDevSkyBackground === "function") game.toggleDevSkyBackground();
    });
  }
  if (dom.devPerfStarsBtn) {
    dom.devPerfStarsBtn.addEventListener("click", function () {
      if (typeof game.toggleDevSkyStars === "function") game.toggleDevSkyStars();
    });
  }
  if (dom.devPerfSkyBtn) {
    dom.devPerfSkyBtn.addEventListener("click", function () {
      if (typeof game.toggleDevSkyDynamic === "function") game.toggleDevSkyDynamic();
    });
  }
  if (dom.devPerfSoundsBtn) {
    dom.devPerfSoundsBtn.addEventListener("click", function () {
      if (typeof game.toggleDevSounds === "function") game.toggleDevSounds();
    });
  }
  if (dom.devPerfParticlesBtn) {
    dom.devPerfParticlesBtn.addEventListener("click", function () {
      if (typeof game.toggleDevParticles === "function") game.toggleDevParticles();
    });
  }
  if (dom.devPerfPopupsBtn) {
    dom.devPerfPopupsBtn.addEventListener("click", function () {
      if (typeof game.toggleDevScorePopups === "function") game.toggleDevScorePopups();
    });
  }
  if (dom.devPerfLayoutBtn) {
    dom.devPerfLayoutBtn.addEventListener("click", function () {
      if (typeof game.cycleDevLayoutPreference === "function") game.cycleDevLayoutPreference();
    });
  }
  if (dom.devPerfBoundsBtn) {
    dom.devPerfBoundsBtn.addEventListener("click", function () {
      if (typeof game.toggleDevLayoutBounds === "function") game.toggleDevLayoutBounds();
    });
  }
  if (dom.devPerfFallCollidersBtn) {
    dom.devPerfFallCollidersBtn.addEventListener("click", function () {
      if (typeof game.toggleDevFallColliders === "function") game.toggleDevFallColliders();
    });
  }

  if (typeof document !== "undefined") {
    document.addEventListener("keydown", function (e) {
      if (!e || e.key !== "Escape") return;
      if (!dom.devPerfDialog || dom.devPerfDialog.classList.contains("hidden")) return;
      if (typeof game.closeDevPerfDialog === "function") game.closeDevPerfDialog();
    });
  }
}